/**
 * NotificationBell Component
 *
 * Bell icon button that shows unread notification count and opens the notification panel.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { useUnreadCount } from '@/hooks/useNotifications';
import { BellIcon } from './notificationIcons';
import NotificationPanel from './NotificationPanel';

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data } = useUnreadCount();
  const unreadCount = data?.count ?? 0;

  // Format display count (show "9+" for counts > 9)
  const displayCount = unreadCount > 9 ? '9+' : unreadCount.toString();

  /**
   * Toggle the notification panel
   */
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  /**
   * Close the notification panel
   */
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Handle notification click (closes panel)
   */
  const handleNotificationClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle escape key to close panel and return focus to button
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={cn(
          'relative p-2 rounded-lg',
          'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          'transition-colors'
        )}
        aria-label={
          unreadCount > 0
            ? `Notifications (${unreadCount} unread)`
            : 'Notifications'
        }
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <BellIcon className="h-5 w-5" />

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute -top-1 -right-1',
              'h-5 min-w-[1.25rem] px-1',
              'flex items-center justify-center',
              'text-xs font-medium text-white',
              'bg-red-500 rounded-full'
            )}
            aria-hidden="true"
          >
            {displayCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <NotificationPanel
          onClose={handleClose}
          onNotificationClick={handleNotificationClick}
        />
      )}
    </div>
  );
}
